import React, { useState, useRef, useEffect } from 'react';
import { Search, MapPin, Briefcase, ChevronDown, X, Check, LayoutGrid, Banknote, Search as SearchIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useProvinces, useDistricts, useWards } from '@/hooks/useAddress';
import { useJobCategories } from '@/hooks/useJob';

const JOB_TYPES = [
  { value: 'full-time', label: 'Toàn thời gian' },
  { value: 'part-time', label: 'Bán thời gian' },
  { value: 'remote', label: 'Remote' },
  { value: 'freelance', label: 'Freelance' },
];

const SALARY_RANGES = [
  { label: 'Tất cả', min: null, max: null },
  { label: 'Dưới 10 triệu', min: 0, max: 10000000 },
  { label: '10 - 15 triệu', min: 10000000, max: 15000000 },
  { label: '15 - 20 triệu', min: 15000000, max: 20000000 },
  { label: '20 - 25 triệu', min: 20000000, max: 25000000 },
  { label: '25 - 30 triệu', min: 25000000, max: 30000000 },
  { label: '30 - 50 triệu', min: 30000000, max: 50000000 },
  { label: 'Trên 50 triệu', min: 50000000, max: null },
  { label: 'Thoả thuận', min: 0, max: 0 },
];

interface JobFilterProps {
  onSearch: (filters: any) => void;
  initialFilters?: any;
}

export const JobFilter: React.FC<JobFilterProps> = ({ onSearch, initialFilters = {} }) => {
  const [search, setSearch] = useState(initialFilters.search || '');
  const [jobType, setJobType] = useState<string | null>(initialFilters.jobType || null);

  // Categories State
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>(initialFilters.jobCategoryId ? [initialFilters.jobCategoryId] : []);
  const [hoveredParentCategory, setHoveredParentCategory] = useState<any>(null);

  // Location State
  const [provinceId, setProvinceId] = useState<number | null>(initialFilters.provinceId || null);
  const [districtId, setDistrictId] = useState<number | null>(initialFilters.districtId || null);
  const [wardId, setWardId] = useState<number | null>(initialFilters.wardId || null);
  const [selectedDistrictIds, setSelectedDistrictIds] = useState<number[]>(initialFilters.districtId ? [initialFilters.districtId] : []);

  const [minSalary, setMinSalary] = useState<number | null>(initialFilters.minSalary || null);
  const [maxSalary, setMaxSalary] = useState<number | null>(initialFilters.maxSalary || null);

  const [activeDropdown, setActiveDropdown] = useState<'industry' | 'location' | 'salary' | 'type' | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Data Hooks
  const { data: categories = [] } = useJobCategories();
  const { data: provinces = [] } = useProvinces(1); // Assuming countryId 1 is Vietnam
  const { data: districts = [] } = useDistricts(provinceId || undefined);
  const { data: wards = [] } = useWards(districtId || undefined);

  useEffect(() => {
    if (categories.length > 0 && !hoveredParentCategory) {
      setHoveredParentCategory(categories[0]);
    }
  }, [categories]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = () => {
    onSearch({
      search: search.trim() || undefined,
      jobType: jobType || undefined,
      jobCategoryId: selectedCategoryIds[0] || undefined,
      provinceId: provinceId || undefined,
      districtId: selectedDistrictIds[0] || undefined,
      wardId: wardId || undefined,
      minSalary: minSalary || undefined,
      maxSalary: maxSalary || undefined,
    });
    setActiveDropdown(null);
  };

  const toggleCategory = (id: string) => {
    setSelectedCategoryIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [id] // Single for now based on API example
    );
  };

  const toggleDistrict = (id: number) => {
    setSelectedDistrictIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [id]
    );
  };

  const resetAll = () => {
    setSearch('');
    setJobType(null);
    setSelectedCategoryIds([]);
    setProvinceId(null);
    setDistrictId(null);
    setWardId(null);
    setSelectedDistrictIds([]);
    setMinSalary(null);
    setMaxSalary(null);

    onSearch({
      search: undefined,
      jobType: undefined,
      jobCategoryId: undefined,
      provinceId: undefined,
      districtId: undefined,
      wardId: undefined,
      minSalary: undefined,
      maxSalary: undefined,
    });
    setActiveDropdown(null);
  };

  return (
    <div className="w-full max-w-7xl mx-auto" ref={containerRef}>
      <div className="relative flex flex-col md:flex-row items-stretch bg-white rounded-2xl shadow-xl border border-gray-100 p-2 gap-2 md:gap-0">

        {/* Category Selector */}
        <div className="relative flex-1 group">
          <button
            onClick={() => setActiveDropdown(activeDropdown === 'industry' ? null : 'industry')}
            className={cn(
              "flex items-center w-full h-12 px-2 gap-1.5 rounded-xl transition-colors text-left",
              "hover:bg-gray-50",
              activeDropdown === 'industry' && "bg-gray-50"
            )}
          >
            <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center text-orange-500 shrink-0">
              <LayoutGrid size={18} />
            </div>
            <div className="flex-1 overflow-hidden">
              {/* <p className="text-[10px]  tracking-wider font-bold text-gray-400 leading-none mb-1">Ngành nghề</p> */}
              <p className="text-sm  font-semibold text-gray-700 truncate">
                {selectedCategoryIds.length > 0 ? `Đã chọn ${selectedCategoryIds.length}` : "Tất cả ngành nghề"}
              </p>
            </div>
            <div className="flex items-center gap-1">
              {selectedCategoryIds.length > 0 && (
                <div
                  onClick={(e) => { e.stopPropagation(); setSelectedCategoryIds([]); handleSearch(); }}
                  className="w-5 h-5 rounded-full hover:bg-gray-200 flex items-center justify-center text-gray-400 transition-colors"
                >
                  <X size={12} />
                </div>
              )}
              <ChevronDown size={16} className={cn("text-gray-400 transition-transform", activeDropdown === 'industry' && "rotate-180")} />
            </div>
          </button>

          {/* Industry Dropdown */}
          {activeDropdown === 'industry' && (
            <div className="absolute top-full left-0 mt-3 w-full md:w-[800px] bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden flex flex-col animate-in fade-in slide-in-from-top-2 duration-200">
              <div className="p-4 border-b flex items-center justify-between bg-gray-50/50">
                <h3 className="font-bold text-gray-800">Chọn Nhóm nghề, Nghề hoặc Chuyên môn</h3>
                <button onClick={() => setSelectedCategoryIds([])} className="text-xs text-orange-500 font-medium hover:underline">Xóa chọn</button>
              </div>

              <div className="flex h-[300px]">
                {/* Left: Parent Categories */}
                <div className="w-1/3 border-r overflow-y-auto custom-scrollbar bg-gray-50/30">
                  <div className="p-2">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest p-2">NHÓM NGHỀ</p>
                    {categories.map((cat: any) => (
                      <div
                        key={cat.id}
                        onMouseEnter={() => setHoveredParentCategory(cat)}
                        className={cn(
                          "flex items-center justify-between px-3 py-2.5 rounded-lg text-sm cursor-pointer transition-all",
                          hoveredParentCategory?.id === cat.id ? "bg-white shadow-sm text-orange-600 font-semibold" : "text-gray-600 hover:bg-white/50"
                        )}
                      >
                        <span className="truncate">{cat.name}</span>
                        <ChevronDown size={14} className="-rotate-90 text-gray-300" />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right: Child Categories */}
                <div className="w-2/3 overflow-y-auto custom-scrollbar p-4">
                  {hoveredParentCategory ? (
                    <div className="space-y-6">
                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">NGHỀ & CHUYÊN MÔN</p>
                        <div className="grid grid-cols-2 gap-2">
                          {hoveredParentCategory.jobChilds?.map((child: any) => (
                            <label
                              key={child.id}
                              className={cn(
                                "flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer",
                                selectedCategoryIds.includes(child.id)
                                  ? "border-orange-500 bg-orange-50/50"
                                  : "border-gray-100 hover:border-orange-200 hover:bg-gray-50"
                              )}
                            >
                              <div className={cn(
                                "w-5 h-5 rounded border flex items-center justify-center transition-all",
                                selectedCategoryIds.includes(child.id) ? "bg-orange-500 border-orange-500" : "bg-white border-gray-300"
                              )}>
                                {selectedCategoryIds.includes(child.id) && <Check size={14} className="text-white" />}
                              </div>
                              <input
                                type="checkbox"
                                className="hidden"
                                checked={selectedCategoryIds.includes(child.id)}
                                onChange={() => toggleCategory(child.id)}
                              />
                              <span className="text-sm text-gray-700 font-medium">{child.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-gray-400 space-y-3">
                      <Briefcase size={40} className="opacity-20" />
                      <p className="text-sm">Vui lòng chọn Nhóm nghề</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-4 bg-gray-50 border-t flex items-center justify-between">
                <p className="text-xs text-gray-500"></p>
                <div className="flex gap-2">
                  <button onClick={() => setActiveDropdown(null)} className="px-6 h-10 rounded-xl border border-gray-200 text-sm font-bold text-gray-600 hover:bg-white transition-colors">Hủy</button>
                  <button onClick={handleSearch} className="px-8 h-10 rounded-xl bg-orange-500 text-sm font-bold text-white hover:bg-orange-600 transition-all shadow-md shadow-orange-200">Chọn</button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="hidden md:block w-px h-8 self-center bg-gray-100 mx-1" />

        {/* Search Input */}
        <div className="flex-[1.2] flex items-center px-3 group min-w-0">
          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-500 shrink-0 mr-2">
            <SearchIcon size={18} />
          </div>
          <div className="flex-1 relative">
            {/* <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400 leading-none mb-1">Vị trí tuyển dụng</p> */}
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Nhập từ khóa tìm kiếm..."
              className="w-full text-sm font-semibold text-gray-700 bg-transparent outline-none placeholder:text-gray-300 pr-8"
            />
            {search && (
              <button
                onClick={() => { setSearch(''); onSearch({ ...initialFilters, search: undefined }); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400 transition-colors"
              >
                <X size={12} />
              </button>
            )}
          </div>
        </div>

        <div className="hidden md:block w-px h-8 self-center bg-gray-100 mx-1" />

        {/* Location Selector */}
        <div className="relative flex-1 group">
          <button
            onClick={() => setActiveDropdown(activeDropdown === 'location' ? null : 'location')}
            className={cn(
              "flex items-center w-full h-12 px-2 gap-1.5 rounded-xl transition-colors text-left",
              "hover:bg-gray-50",
              activeDropdown === 'location' && "bg-gray-50"
            )}
          >
            <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center text-green-500 shrink-0">
              <MapPin size={18} />
            </div>
            <div className="flex-1 overflow-hidden">
              {/* <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400 leading-none mb-1">Địa điểm</p> */}
              <p className="text-sm font-semibold text-gray-700 truncate">
                {provinceId ? provinces.find((p: any) => p.id === provinceId)?.name : "Toàn quốc"}
              </p>
            </div>
            <div className="flex items-center gap-1">
              {provinceId !== null && (
                <div
                  onClick={(e) => { e.stopPropagation(); setProvinceId(null); setSelectedDistrictIds([]); handleSearch(); }}
                  className="w-5 h-5 rounded-full hover:bg-gray-200 flex items-center justify-center text-gray-400 transition-colors"
                >
                  <X size={12} />
                </div>
              )}
              <ChevronDown size={16} className={cn("text-gray-400 transition-transform", activeDropdown === 'location' && "rotate-180")} />
            </div>
          </button>

          {/* Location Dropdown */}
          {activeDropdown === 'location' && (
            <div className="absolute top-full right-0 mt-3 w-full md:w-[700px] bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden flex flex-col animate-in fade-in slide-in-from-top-2 duration-200">
              <div className="p-4 border-b flex items-center justify-between bg-gray-50/50">
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="loc-type" defaultChecked className="accent-orange-500" />
                    <span className="text-sm font-medium text-gray-700">Tỉnh, Quận/huyện cũ</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer opacity-50">
                    <input type="radio" name="loc-type" className="accent-orange-500" />
                    <span className="text-sm font-medium text-gray-700">Tỉnh, Phường/xã mới</span>
                  </label>
                </div>
                <button onClick={() => { setProvinceId(null); setSelectedDistrictIds([]); }} className="text-xs text-orange-500 font-medium hover:underline">Xóa chọn</button>
              </div>

              <div className="flex h-[300px] ">
                {/* Left: Provinces */}
                <div className="w-2/5 border-r overflow-y-auto custom-scrollbar">
                  <div className="p-2 space-y-1">
                    {provinces.map((p: any) => (
                      <div
                        key={p.id}
                        onClick={() => setProvinceId(p.id)}
                        className={cn(
                          "flex items-center justify-between px-3 py-2.5 rounded-lg text-sm cursor-pointer transition-all",
                          provinceId === p.id ? "bg-orange-50 text-orange-600 font-semibold" : "text-gray-600 hover:bg-gray-50"
                        )}
                      >
                        <span className="truncate">{p.name}</span>
                        <ChevronDown size={14} className="-rotate-90 text-gray-300" />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right: Districts */}
                <div className="w-3/5 overflow-y-auto custom-scrollbar p-4">
                  {provinceId ? (
                    <div className="grid grid-cols-1 gap-1">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">QUẬN / HUYỆN</p>
                      {districts.map((d: any) => (
                        <label
                          key={d.id}
                          className={cn(
                            "flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-all cursor-pointer",
                            selectedDistrictIds.includes(d.id) ? "bg-orange-50 text-orange-600 font-semibold" : "text-gray-600 hover:bg-gray-50"
                          )}
                        >
                          <span>{d.name}</span>
                          <div className={cn(
                            "w-4 h-4 rounded border flex items-center justify-center",
                            selectedDistrictIds.includes(d.id) ? "bg-orange-500 border-orange-500" : "border-gray-300"
                          )}>
                            {selectedDistrictIds.includes(d.id) && <Check size={12} className="text-white" />}
                          </div>
                          <input
                            type="checkbox"
                            className="hidden"
                            checked={selectedDistrictIds.includes(d.id)}
                            onChange={() => toggleDistrict(d.id)}
                          />
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-gray-400 space-y-3">
                      <MapPin size={40} className="opacity-20" />
                      <p className="text-sm">Vui lòng chọn Tỉnh/Thành phố</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-4 bg-gray-50 border-t flex items-center justify-end gap-2">
                <button onClick={() => setActiveDropdown(null)} className="px-6 h-10 rounded-xl border border-gray-200 text-sm font-bold text-gray-600 hover:bg-white transition-colors">Hủy</button>
                <button onClick={handleSearch} className="px-8 h-10 rounded-xl bg-orange-500 text-sm font-bold text-white hover:bg-orange-600 transition-all shadow-md shadow-orange-200">Áp dụng</button>
              </div>
            </div>
          )}
        </div>

        <div className="hidden md:block w-px h-8 self-center bg-gray-100 mx-1" />

        {/* Salary Selector */}
        <div className="relative flex-1 group">
          <button
            onClick={() => setActiveDropdown(activeDropdown === 'salary' ? null : 'salary')}
            className={cn(
              "flex items-center w-full h-12 px-2 gap-1.5 rounded-xl transition-colors text-left",
              "hover:bg-gray-50",
              activeDropdown === 'salary' && "bg-gray-50"
            )}
          >
            <div className="w-8 h-8 rounded-lg bg-yellow-50 flex items-center justify-center text-yellow-600 shrink-0">
              <Banknote size={18} />
            </div>
            <div className="flex-1 overflow-hidden">
              {/* <p className="text-[10px] tracking-wider font-bold text-gray-400 leading-none mb-1">MỨC LƯƠNG</p> */}
              <p className="text-sm font-semibold text-gray-700 truncate">
                {minSalary === null && maxSalary === null ? "Mức lương" :
                  minSalary === 0 && maxSalary === 0 ? "Thoả thuận" :
                    minSalary !== null && maxSalary !== null ? `${minSalary / 1000000} - ${maxSalary / 1000000}tr` :
                      minSalary !== null ? `Trên ${minSalary / 1000000}tr` :
                        maxSalary !== null ? `Dưới ${maxSalary / 1000000}tr` : "Mức lương"}
              </p>
            </div>
            <div className="flex items-center gap-1">
              {(minSalary !== null || maxSalary !== null) && (
                <div
                  onClick={(e) => { e.stopPropagation(); setMinSalary(null); setMaxSalary(null); handleSearch(); }}
                  className="w-5 h-5 rounded-full hover:bg-gray-200 flex items-center justify-center text-gray-400 transition-colors"
                >
                  <X size={12} />
                </div>
              )}
              <ChevronDown size={16} className={cn("text-gray-400 transition-transform", activeDropdown === 'salary' && "rotate-180")} />
            </div>
          </button>

          {/* Salary Dropdown */}
          {activeDropdown === 'salary' && (
            <div className="absolute top-full left-0 mt-3 w-full md:w-[450px] bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden flex flex-col animate-in fade-in slide-in-from-top-2 duration-200">
              <div className="p-4 border-b flex items-center justify-between bg-gray-50/50">
                <h3 className="font-bold text-gray-800">Chọn mức lương</h3>
                <button onClick={() => { setMinSalary(null); setMaxSalary(null); }} className="text-xs text-orange-500 font-medium hover:underline">Xóa chọn</button>
              </div>

              <div className="max-h-[300px] overflow-y-auto custom-scrollbar p-5">
                <div className="grid grid-cols-2 gap-2 mb-6">
                  {SALARY_RANGES.map((range, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setMinSalary(range.min);
                        setMaxSalary(range.max);
                      }}
                      className={cn(
                        "flex items-center gap-2 p-2.5 rounded-xl border text-left transition-all",
                        (minSalary === range.min && maxSalary === range.max)
                          ? "border-orange-500 bg-orange-50/50 text-orange-600 font-semibold"
                          : "border-gray-100 hover:border-orange-200 hover:bg-gray-50 text-gray-600"
                      )}
                    >
                      <div className={cn(
                        "w-4 h-4 rounded-full border flex items-center justify-center transition-all",
                        (minSalary === range.min && maxSalary === range.max) ? "border-orange-500 border-4" : "border-gray-300"
                      )} />
                      <span className="text-sm">{range.label}</span>
                    </button>
                  ))}
                </div>

                <div className="border-t pt-4">
                  <p className="text-sm font-semibold text-gray-700 mb-3">Hoặc nhập khoảng lương (triệu VNĐ)</p>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 relative">
                      <input
                        type="number"
                        placeholder="Từ"
                        value={minSalary ? minSalary / 1000000 : ''}
                        onChange={(e) => setMinSalary(e.target.value ? Number(e.target.value) * 1000000 : null)}
                        className="w-full h-10 px-3 rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none text-sm"
                      />
                    </div>
                    <span className="text-gray-400">-</span>
                    <div className="flex-1 relative">
                      <input
                        type="number"
                        placeholder="Đến"
                        value={maxSalary ? maxSalary / 1000000 : ''}
                        onChange={(e) => setMaxSalary(e.target.value ? Number(e.target.value) * 1000000 : null)}
                        className="w-full h-10 px-3 rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-gray-50 border-t flex items-center justify-end gap-2">
                <button onClick={() => setActiveDropdown(null)} className="px-6 h-10 rounded-xl border border-gray-200 text-sm font-bold text-gray-600 hover:bg-white transition-colors">Hủy</button>
                <button onClick={handleSearch} className="px-8 h-10 rounded-xl bg-orange-500 text-sm font-bold text-white hover:bg-orange-600 transition-all shadow-md shadow-orange-200">Áp dụng</button>
              </div>
            </div>
          )}
        </div>

        <div className="hidden md:block w-px h-8 self-center bg-gray-100 mx-1" />

        {/* Job Type Selector */}
        <div className="relative flex-1 group">
          <button
            onClick={() => setActiveDropdown(activeDropdown === 'type' ? null : 'type')}
            className={cn(
              "flex items-center w-full h-12 px-2 gap-1.5 rounded-xl transition-colors text-left",
              "hover:bg-gray-50",
              activeDropdown === 'type' && "bg-gray-50"
            )}
          >
            <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center text-purple-500 shrink-0">
              <Briefcase size={18} />
            </div>
            <div className="flex-1 overflow-hidden">
              {/* <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400 leading-none mb-1">Hình thức</p> */}
              <p className="text-sm font-semibold text-gray-700 truncate">
                {jobType ? JOB_TYPES.find(t => t.value === jobType)?.label : "Tất cả"}
              </p>
            </div>
            <ChevronDown size={16} className={cn("text-gray-400 transition-transform", activeDropdown === 'type' && "rotate-180")} />
          </button>

          {/* Type Dropdown */}
          {activeDropdown === 'type' && (
            <div className="absolute top-full right-0 mt-2 w-full md:w-[220px] bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 p-4 animate-in fade-in slide-in-from-top-2 duration-200">
              <div className="grid grid-cols-1 gap-1">
                <button
                  onClick={() => { setJobType(null); setActiveDropdown(null); }}
                  className={cn(
                    "flex items-center justify-between px-3 py-2.5 rounded-xl text-sm transition-all",
                    !jobType ? "bg-orange-50 text-orange-600 font-semibold" : "text-gray-600 hover:bg-gray-50"
                  )}
                >
                  <span>Tất cả hình thức</span>
                  {!jobType && <Check size={16} />}
                </button>
                {JOB_TYPES.map((item) => (
                  <button
                    key={item.value}
                    onClick={() => { setJobType(item.value); setActiveDropdown(null); }}
                    className={cn(
                      "flex items-center justify-between px-3 py-2.5 rounded-xl text-sm transition-all",
                      jobType === item.value
                        ? "bg-orange-50 text-orange-600 font-semibold"
                        : "text-gray-600 hover:bg-gray-50"
                    )}
                  >
                    <span>{item.label}</span>
                    {jobType === item.value && <Check size={16} />}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Search Button */}
        <div className="p-1 flex gap-2">
          <button
            onClick={resetAll}
            className={cn(
              "hidden lg:flex items-center justify-center gap-1 h-10 md:h-full px-4 rounded-xl border",
              "text-gray-500 text-sm font-medium hover:bg-gray-50 transition-colors"
            )}
            title="Xóa lọc"
          >
            <X size={16} />
          </button>
          <button
            onClick={handleSearch}
            className={cn(
              "flex-1 md:flex-none flex items-center justify-center gap-2 h-10 md:h-full px-8 rounded-xl",
              "bg-orange-500 text-white font-bold text-sm shadow-lg shadow-orange-100",
              "hover:bg-orange-600 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200"
            )}
          >
            <SearchIcon size={18} />
            <span className="md:hidden lg:inline">Tìm kiếm</span>
          </button>
        </div>
      </div>

      {/* Quick filters (Tags) */}
      {/* <div className="flex items-center gap-2 mt-4 overflow-x-auto pb-2 no-scrollbar">
        {[
          { label: 'Việc làm từ xa', icon: '🏠' },
          { label: 'Bán thời gian', icon: '🕒' },
          { label: 'Thực tập sinh', icon: '🎓' },
          { label: 'Mức lương cao', icon: '💰' },
          { label: 'Không yêu cầu kinh nghiệm', icon: '✨' },
        ].map((tag, idx) => (
          <button
            key={idx}
            className="shrink-0 flex items-center gap-2 px-4 py-1.5 bg-white border border-gray-100 rounded-full text-[13px] font-medium text-gray-600 hover:border-orange-300 hover:text-orange-500 transition-all shadow-sm"
          >
            <span>{tag.icon}</span>
            <span>{tag.label}</span>
          </button>
        ))}
      </div> */}
    </div>
  );
};
